import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { deliveryActionLabel } from "@/lib/orderLabels";
import { currentStep, isDisputed, journeyStageLabel } from "@/lib/orderJourney";
import { Download } from "lucide-react";
import DataList from "@/components/DataList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Sparkline({ order }) {
  const idx = currentStep(order);
  const disp = isDisputed(order);
  return (
    <span className="spark" data-testid={`spark-${order.code}`} aria-label={`Step ${idx + 1} of 7`}>
      {Array.from({ length: 7 }).map((_, i) => {
        const cls = disp && i === idx ? "spark-pip disputed"
          : i < idx ? "spark-pip done"
          : i === idx ? "spark-pip current"
          : "spark-pip";
        return <span key={i} className={cls} />;
      })}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: "all", label: "ALL" },
  { value: "pending_payment", label: "AWAITING INVOICE/PAY" },
  { value: "payment_received", label: "PAYMENT SENT" },
  { value: "processing", label: "IN PROGRESS" },
  { value: "shipped", label: "IN TRANSIT" },
  { value: "delivered", label: "DELIVERED" },
  { value: "closed", label: "CLOSED" },
  { value: "disputed", label: "DISPUTED" },
];

export default function OrdersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const load = () => api.get("/orders").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (oid, status) => {
    try {
      const tracking = status === "shipped" ? window.prompt("Tracking number?") : null;
      await api.post(`/orders/${oid}/status`, { status, tracking_number: tracking });
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };

  // Backend transition rules per role. UI labels come from deliveryActionLabel.
  const nextStatuses = (o) => {
    const s = o.status;
    if (user.role === "buyer") {
      if (s === "pending_payment") return ["payment_received"];
      if (s === "delivered") return ["closed", "disputed"];
    }
    if (user.role === "supplier") {
      if (s === "payment_received") return ["processing"];
      if (s === "processing") return ["shipped"];
      if (s === "shipped") return ["delivered"];
    }
    return [];
  };

  const columns = [
    { key: "code", label: "PO",
      render: o => (
        <Link to={`/app/orders/${o.id}`} data-testid={`order-link-${o.code}`}
              className="font-mono" style={{ color: "var(--amber-400)" }}>{o.code}</Link>
      ),
      search: o => o.code },
    { key: "product", label: "Product", render: o => o.product_name, search: o => o.product_name },
    { key: "qty", label: "Qty", render: o => `${o.quantity} ${o.unit}` },
    { key: "total", label: "Total", render: o => <span style={{ fontWeight: 600 }}>${o.total_price.toFixed(2)}</span> },
    { key: "progress", label: "Progress", render: o => <Sparkline order={o} /> },
    { key: "stage", label: "Stage",
      render: o => <span className={`pill pill-${isDisputed(o) ? "disputed" : o.status}`} data-testid={`stage-${o.code}`}>{journeyStageLabel(o, user.role)}</span> },
    { key: "actions", label: "Actions",
      render: o => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {nextStatuses(o).map(s => {
            const label = deliveryActionLabel(s, user.role);
            if (!label) return null;
            return (
              <button key={s} data-testid={`order-${o.code}-${s}`} onClick={() => setStatus(o.id, s)} className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }}>
                → {label}
              </button>
            );
          })}
        </div>
      ) },
  ];

  const filters = [
    { key: "status", label: "Status", options: STATUS_OPTIONS,
      match: (row, value) => {
        if (value === "all") return true;
        if (value === "disputed") return isDisputed(row);
        return row.status === value;
      } },
  ];

  return (
    <>
      <PageHeader title="Orders" subtitle="Awarded RFQs become trackable orders." actions={
        <a href={`${BACKEND_URL}/api/export/orders.csv`} target="_blank" rel="noreferrer" data-testid="export-orders-csv" className="btn btn-ghost"><Download size={14} /> Export CSV</a>
      } />
      {err && <div style={{ color: "var(--red)", marginBottom: 12 }}>{err}</div>}
      <DataList
        testId="orders-list"
        rows={rows}
        columns={columns}
        filters={filters}
        emptyText="No orders yet."
        searchPlaceholder="Search by PO or product…"
      />
    </>
  );
}
